import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import "./AdminHostingSetup.css";
import { connectRealtimeSocket } from "../../ServerPackage/CompleteCPanel/utils/realtimeSocket";
import Navbar from "../../Common/Navbar";

/* ==============================
   API BASE
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiSuffix = (base) => {
  const clean = normalizeBase(base);
  if (!clean) return "http://localhost:5000/api";
  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiSuffix(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH HELPERS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_KEYS_TO_CLEAR = [
  ...AUTH_TOKEN_KEYS,
  "isLoggedIn",
  "user",
  "currentUser",
  "email",
  "userEmail",
  "user_email",
  "fullName",
  "name",
  "userName",
  "username",
];

const getStorageValue = (key) => {
  return localStorage.getItem(key) || sessionStorage.getItem(key) || "";
};

const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const value = getStorageValue(key);

    if (value && String(value).trim()) {
      return String(value).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_KEYS_TO_CLEAR.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  window.dispatchEvent(
    new CustomEvent("auth-state-changed", {
      detail: {
        source: "AdminHostingSetup",
        action: "clear-auth",
      },
    })
  );
};

const safeJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const getStoredUser = () => {
  const raw =
    localStorage.getItem("user") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("currentUser") ||
    sessionStorage.getItem("currentUser") ||
    "";

  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

/* ==============================
   URL HELPERS
============================== */
const normalizeDomainName = (value = "") => {
  return String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/, "")
    .toLowerCase();
};

const buildLiveUrl = (domain = "") => {
  const clean = normalizeDomainName(domain);
  return clean ? `https://${clean}` : "";
};

/* ==============================
   OPTIONS
============================== */
const setupStatusOptions = [
  { value: "pending_setup", label: "Pending Setup" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
];

const serviceStatusOptions = [
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "suspended", label: "Suspended" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
];

const requestStatusOptions = [
  { value: "submitted", label: "Submitted" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "rejected", label: "Rejected" },
];

const initialSetupForm = {
  setupStatus: "completed",
  cpanelUrl: "",
  cpanelUsername: "",
  serverIp: "",
  nameserver1: "",
  nameserver2: "",
};

const getSetupLabel = (value = "") => {
  const found = setupStatusOptions.find((item) => item.value === value);
  return found?.label || "Pending Setup";
};

const getStatusLabel = (value = "") => {
  const found = serviceStatusOptions.find((item) => item.value === value);
  return found?.label || "Active";
};

const getRequestStatusLabel = (value = "") => {
  const found = requestStatusOptions.find((item) => item.value === value);
  return found?.label || "No Request";
};

const getSetupClass = (value = "") => {
  const clean = String(value || "").toLowerCase();

  if (clean === "completed") return "completed";
  if (clean === "in_progress") return "progress";
  if (clean === "failed" || clean === "rejected") return "failed";
  if (clean === "submitted") return "progress";

  return "pending";
};

const normalizeSetupRequest = (request = null) => {
  if (!request || typeof request !== "object") return null;

  const domainName = normalizeDomainName(
    request.domainName || request.domain_name || ""
  );

  const liveUrl =
    request.liveUrl ||
    request.live_url ||
    request.customDomainUrl ||
    request.custom_domain_url ||
    buildLiveUrl(domainName);

  return {
    id: request.id || request.request_id || null,
    userId: request.userId || request.user_id || null,
    serviceId: request.serviceId || request.service_id || null,
    domainName,
    websiteType: request.websiteType || request.website_type || "business",
    businessName: request.businessName || request.business_name || "",

    needEmail: false,
    emailRequired: false,

    needSsl: true,
    sslRequired: true,

    liveUrl,
    customDomainUrl: liveUrl,

    notes: request.notes || "",
    status: request.status || "completed",
    createdAt: request.createdAt || request.created_at || "",
    updatedAt: request.updatedAt || request.updated_at || "",
    createdAtFormatted:
      request.createdAtFormatted || request.created_at_formatted || "",
    updatedAtFormatted:
      request.updatedAtFormatted || request.updated_at_formatted || "",
  };
};

const normalizeService = (item = {}) => {
  const setupRequest = normalizeSetupRequest(
    item.setupRequest || item.setup_request || null
  );

  const customDomain = normalizeDomainName(
    item.customDomain ||
      item.custom_domain ||
      item.domainName ||
      item.domain_name ||
      setupRequest?.domainName ||
      ""
  );

  const liveUrl =
    item.liveUrl ||
    item.live_url ||
    item.customDomainUrl ||
    item.custom_domain_url ||
    setupRequest?.liveUrl ||
    buildLiveUrl(customDomain);

  return {
    id: item.id,
    userId: item.userId || item.user_id,
    user: item.user || {},
    planKey: item.planKey || item.plan_key || "",
    planName: item.planName || item.plan_name || "Hosting Plan",
    price: Number(item.price || 0),
    billingCycle: item.billingCycle || item.billing_cycle || "monthly",
    status: item.status || "active",
    setupStatus: item.setupStatus || item.setup_status || "completed",
    websiteLimit: Number(item.websiteLimit || item.website_limit || 0),
    storageLimit: item.storageLimit || item.storage_limit || "",
    mailboxLimit: Number(item.mailboxLimit || item.mailbox_limit || 0),
    sslEnabled: true,
    cpanelUrl: item.cpanelUrl || item.cpanel_url || "",
    cpanelUsername: item.cpanelUsername || item.cpanel_username || "",
    serverIp: item.serverIp || item.server_ip || "",
    nameserver1: item.nameserver1 || item.nameserver_1 || "",
    nameserver2: item.nameserver2 || item.nameserver_2 || "",

    customDomain,
    liveUrl,
    customDomainUrl: liveUrl,

    sslRequired: true,
    needSsl: true,
    emailRequired: false,
    needEmail: false,

    startedAt: item.startedAt || item.started_at || "",
    expiresAt: item.expiresAt || item.expires_at || "",
    createdAtFormatted: item.createdAtFormatted || "",
    updatedAtFormatted: item.updatedAtFormatted || "",
    setupRequest,
  };
};

function AdminHostingSetup() {
  const navigate = useNavigate();

  const [adminUser, setAdminUser] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [setupForm, setSetupForm] = useState(initialSetupForm);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusSavingId, setStatusSavingId] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");

  const [search, setSearch] = useState("");
  const [setupFilter, setSetupFilter] = useState("completed");
  const [statusFilter, setStatusFilter] = useState("active");

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const token = useMemo(() => getToken(), []);

  const authFetch = useCallback(
    async (endpoint, options = {}) => {
      const currentToken = getToken();

      if (!currentToken) {
        clearAuth();
        navigate("/signin", { replace: true });
        throw new Error("Session expired. Please login again.");
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
          ...(options.headers || {}),
        },
      });

      const data = await safeJson(response);

      if (response.status === 401) {
        clearAuth();
        navigate("/signin", { replace: true });
        throw new Error("Session expired. Please login again.");
      }

      if (response.status === 403) {
        throw new Error("Admin access required. This page is restricted.");
      }

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Request failed.");
      }

      return data;
    },
    [navigate]
  );

  const checkAdminProfile = useCallback(async () => {
    const data = await authFetch("/auth/me");
    const profile = data?.data || data?.user || {};
    const stored = getStoredUser();

    const role = String(profile.role || stored.role || "user").toLowerCase();

    if (role !== "admin" && role !== "superadmin") {
      throw new Error("Admin access required. You cannot access this page.");
    }

    setAdminUser({
      id: profile.id || stored.id,
      fullName:
        profile.fullName || profile.full_name || stored.fullName || "Admin",
      email: profile.email || stored.email || "",
      role,
    });
  }, [authFetch]);

  const loadServices = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setError("");
        setMessage("");

        const params = new URLSearchParams();

        if (setupFilter) params.set("setupStatus", setupFilter);
        if (statusFilter) params.set("status", statusFilter);
        if (search.trim()) params.set("search", search.trim());

        const data = await authFetch(
          `/admin/hosting/services?${params.toString()}`
        );

        const rows = Array.isArray(data?.data) ? data.data : [];
        const normalized = rows.map(normalizeService);

        setServices(normalized);

        if (selectedService?.id) {
          const updatedSelected = normalized.find(
            (item) => Number(item.id) === Number(selectedService.id)
          );

          if (updatedSelected) {
            setSelectedService(updatedSelected);
          }
        }
      } catch (err) {
        setError(err.message || "Unable to load hosting services.");
        setServices([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch, search, selectedService?.id, setupFilter, statusFilter]
  );

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        await checkAdminProfile();
        await loadServices({ silent: true });
      } catch (err) {
        setError(err.message || "Admin verification failed.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [checkAdminProfile, loadServices]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadServices({ silent: true });
    }, 450);

    return () => clearTimeout(timer);
  }, [search, setupFilter, statusFilter, loadServices]);

  useEffect(() => {
    if (!token) {
      setRealtimeStatus("offline");
      return undefined;
    }

    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        loadServices({ silent: true });
      }, 600);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleAuthError = () => {
      clearAuth();
      navigate("/signin", { replace: true });
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);
    socket.on("hosting:status", handleRefresh);
    socket.on("dashboard:update", handleRefresh);
    socket.on("notification:new", handleRefresh);
    socket.on("activity:new", handleRefresh);
    socket.on("connect_error", handleAuthError);

    if (socket.connected) handleConnected();

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);
      socket.off("hosting:status", handleRefresh);
      socket.off("dashboard:update", handleRefresh);
      socket.off("notification:new", handleRefresh);
      socket.off("activity:new", handleRefresh);
      socket.off("connect_error", handleAuthError);
    };
  }, [loadServices, navigate, token]);

  const stats = useMemo(() => {
    const total = services.length;
    const pending = services.filter(
      (item) => item.setupStatus === "pending_setup"
    ).length;
    const progress = services.filter(
      (item) => item.setupStatus === "in_progress"
    ).length;
    const completed = services.filter(
      (item) => item.setupStatus === "completed"
    ).length;

    return { total, pending, progress, completed };
  }, [services]);

  const openSetupModal = (service) => {
    const safeService = normalizeService(service);

    setSelectedService(safeService);
    setSetupForm({
      setupStatus:
        safeService.setupStatus === "pending_setup"
          ? "in_progress"
          : safeService.setupStatus || "completed",
      cpanelUrl: safeService.cpanelUrl || "",
      cpanelUsername: safeService.cpanelUsername || "",
      serverIp: safeService.serverIp || "",
      nameserver1: safeService.nameserver1 || "",
      nameserver2: safeService.nameserver2 || "",
    });
    setError("");
    setMessage("");
  };

  const closeSetupModal = () => {
    setSelectedService(null);
    setSetupForm(initialSetupForm);
  };

  const handleSetupFormChange = (e) => {
    const { name, value } = e.target;

    setSetupForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
    setMessage("");
  };

  const handleSaveSetup = async (e) => {
    e.preventDefault();

    if (!selectedService?.id) {
      setError("Please select a hosting service first.");
      return;
    }

    if (
      setupForm.setupStatus === "completed" &&
      (!setupForm.cpanelUrl.trim() || !setupForm.cpanelUsername.trim())
    ) {
      setError("cPanel URL and username are required to mark setup completed.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const data = await authFetch(
        `/admin/hosting/services/${selectedService.id}/setup`,
        {
          method: "PATCH",
          body: JSON.stringify({
            setupStatus: setupForm.setupStatus,
            cpanelUrl: setupForm.cpanelUrl.trim(),
            cpanelUsername: setupForm.cpanelUsername.trim(),
            serverIp: setupForm.serverIp.trim(),
            nameserver1: setupForm.nameserver1.trim(),
            nameserver2: setupForm.nameserver2.trim(),
            needEmail: false,
            needSsl: true,
            emailRequired: false,
            sslRequired: true,
          }),
        }
      );

      const updated = normalizeService(data?.data || {});

      setMessage(
        "Hosting setup updated successfully. Live URL and SSL status are now ready for user panel."
      );
      setSelectedService(updated);

      setServices((prev) =>
        prev.map((item) =>
          Number(item.id) === Number(updated.id) ? updated : item
        )
      );

      setTimeout(() => {
        closeSetupModal();
        loadServices({ silent: true });
      }, 700);
    } catch (err) {
      setError(err.message || "Unable to update hosting setup.");
    } finally {
      setSaving(false);
    }
  };

  const handleChangeServiceStatus = async (serviceId, nextStatus) => {
    if (!serviceId || !nextStatus) return;

    try {
      setStatusSavingId(String(serviceId));
      setError("");
      setMessage("");

      const data = await authFetch(
        `/admin/hosting/services/${serviceId}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({
            status: nextStatus,
          }),
        }
      );

      const updated = normalizeService(data?.data || {});

      setServices((prev) =>
        prev.map((item) =>
          Number(item.id) === Number(updated.id) ? updated : item
        )
      );

      setMessage("Hosting service status updated successfully.");
    } catch (err) {
      setError(err.message || "Unable to update service status.");
    } finally {
      setStatusSavingId("");
    }
  };

  return (
    <>
      <Navbar />

      <section className="admin-hosting-page">
        <div className="admin-hosting-container">
          <div className="admin-hosting-hero">
            <div>
              <span className="admin-hosting-eyebrow">Admin Hosting Setup</span>

              <h1>Hosting Service Setup Panel</h1>

              <p>
                Manage user hosting activation, live URL, custom domain, SSL,
                cPanel access, server IP and nameservers securely from one
                admin-only dashboard.
              </p>

              <div className="admin-hosting-meta">
                <span>
                  {realtimeStatus === "connected"
                    ? "🟢 Realtime connected"
                    : realtimeStatus === "connecting"
                    ? "🟡 Connecting realtime"
                    : "⚪ Realtime offline"}
                </span>

                <span>Admin: {adminUser?.email || "Verifying admin..."}</span>
              </div>
            </div>

            <div className="admin-hosting-actions">
              <button
                type="button"
                onClick={() => loadServices({ silent: true })}
                disabled={refreshing || loading}
              >
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>

              <Link to="/dashboard">User Dashboard</Link>
            </div>
          </div>

          {error ? (
            <div className="admin-hosting-alert error">{error}</div>
          ) : null}

          {message ? (
            <div className="admin-hosting-alert success">{message}</div>
          ) : null}

          <div className="admin-hosting-stats">
            <div>
              <strong>{stats.total}</strong>
              <span>Total Services</span>
            </div>

            <div>
              <strong>{stats.pending}</strong>
              <span>Pending Setup</span>
            </div>

            <div>
              <strong>{stats.progress}</strong>
              <span>In Progress</span>
            </div>

            <div>
              <strong>{stats.completed}</strong>
              <span>Completed</span>
            </div>
          </div>

          <div className="admin-hosting-filters">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by user, email, requested domain, live URL, plan, cPanel username, server IP..."
            />

            <select
              value={setupFilter}
              onChange={(e) => setSetupFilter(e.target.value)}
            >
              <option value="">All setup statuses</option>
              {setupStatusOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All service statuses</option>
              {serviceStatusOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-hosting-table-card">
            <div className="admin-hosting-table-head">
              <h2>Hosting Services</h2>
              <span>{services.length} visible</span>
            </div>

            {loading ? (
              <div className="admin-hosting-loading">
                Loading hosting services...
              </div>
            ) : services.length === 0 ? (
              <div className="admin-hosting-empty">
                No hosting services found for selected filters.
              </div>
            ) : (
              <div className="admin-hosting-table-wrap">
                <table className="admin-hosting-table">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>User</th>
                      <th>Request</th>
                      <th>Live URL</th>
                      <th>Limits</th>
                      <th>Setup</th>
                      <th>Access</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {services.map((service) => (
                      <tr key={service.id}>
                        <td>
                          <div className="admin-service-main">
                            <strong>{service.planName}</strong>
                            <span>
                              ₹{service.price} / {service.billingCycle}
                            </span>
                            <small>ID: {service.id}</small>
                          </div>
                        </td>

                        <td>
                          <div className="admin-user-cell">
                            <strong>
                              {service.user?.fullName ||
                                `User #${service.userId}`}
                            </strong>
                            <span>{service.user?.email || "No email"}</span>
                            <small>{service.user?.phone || "No phone"}</small>
                          </div>
                        </td>

                        <td>
                          {service.setupRequest ? (
                            <div className="admin-request-cell">
                              <strong>
                                {service.setupRequest.domainName || "No domain"}
                              </strong>
                              <span>
                                Type: {service.setupRequest.websiteType}
                              </span>
                              <span>
                                Business:{" "}
                                {service.setupRequest.businessName || "N/A"}
                              </span>
                              <span>SSL: Required</span>
                              <small>
                                Request:{" "}
                                {getRequestStatusLabel(
                                  service.setupRequest.status
                                )}
                              </small>
                            </div>
                          ) : (
                            <div className="admin-request-cell empty">
                              <strong>No setup request</strong>
                              <span>User has not submitted setup form yet.</span>
                            </div>
                          )}
                        </td>

                        <td>
                          <div className="admin-live-url-cell">
                            <strong>
                              {service.liveUrl ? "Live URL Ready" : "Not Ready"}
                            </strong>

                            {service.liveUrl ? (
                              <a
                                href={service.liveUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {service.liveUrl}
                              </a>
                            ) : (
                              <span>Waiting for domain</span>
                            )}

                            <small>
                              {service.setupStatus === "completed"
                                ? "Status: Successfully Completed"
                                : "Status: Connecting"}
                            </small>
                          </div>
                        </td>

                        <td>
                          <div className="admin-limit-cell">
                            <span>{service.websiteLimit} websites</span>
                            <span>
                              {service.storageLimit || "Storage included"}
                            </span>
                            <span>{service.mailboxLimit} mailboxes</span>
                            <span>SSL: Required</span>
                          </div>
                        </td>

                        <td>
                          <span
                            className={`admin-setup-pill ${getSetupClass(
                              service.setupStatus
                            )}`}
                          >
                            {getSetupLabel(service.setupStatus)}
                          </span>
                        </td>

                        <td>
                          <div className="admin-access-cell">
                            <span>
                              cPanel:{" "}
                              {service.cpanelUrl ? (
                                <a
                                  href={service.cpanelUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Open
                                </a>
                              ) : (
                                "Pending"
                              )}
                            </span>
                            <span>
                              User: {service.cpanelUsername || "Not set"}
                            </span>
                            <span>IP: {service.serverIp || "Not set"}</span>
                          </div>
                        </td>

                        <td>
                          <div className="admin-status-cell">
                            <span className="admin-current-status">
                              {getStatusLabel(service.status)}
                            </span>

                            <select
                              className="admin-status-select"
                              value={service.status}
                              disabled={statusSavingId === String(service.id)}
                              title={`Current status: ${getStatusLabel(
                                service.status
                              )}`}
                              aria-label={`Change service status from ${getStatusLabel(
                                service.status
                              )}`}
                              onChange={(e) =>
                                handleChangeServiceStatus(
                                  service.id,
                                  e.target.value
                                )
                              }
                            >
                              {serviceStatusOptions.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </td>

                        <td>
                          <button
                            type="button"
                            className="admin-setup-btn"
                            onClick={() => openSetupModal(service)}
                          >
                            Setup / Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      {selectedService ? (
        <div className="admin-modal-overlay" role="presentation">
          <div className="admin-modal-card">
            <div className="admin-modal-head">
              <div>
                <span>Service #{selectedService.id}</span>
                <h2>{selectedService.planName}</h2>
                <p>
                  User: {selectedService.user?.email || selectedService.userId}
                </p>
              </div>

              <button type="button" onClick={closeSetupModal}>
                ✕
              </button>
            </div>

            {selectedService.setupRequest ? (
              <div className="admin-modal-request-box">
                <span className="admin-hosting-eyebrow">
                  User Setup Request
                </span>

                <h3>{selectedService.setupRequest.domainName}</h3>

                <div className="admin-modal-request-grid">
                  <div>
                    <strong>Website Type</strong>
                    <span>{selectedService.setupRequest.websiteType}</span>
                  </div>

                  <div>
                    <strong>Business Name</strong>
                    <span>
                      {selectedService.setupRequest.businessName || "N/A"}
                    </span>
                  </div>

                  <div>
                    <strong>Live URL</strong>
                    {selectedService.liveUrl ? (
                      <a
                        href={selectedService.liveUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {selectedService.liveUrl}
                      </a>
                    ) : (
                      <span>Not ready</span>
                    )}
                  </div>

                  <div>
                    <strong>SSL Setup</strong>
                    <span>Required</span>
                  </div>

                  <div>
                    <strong>Request Status</strong>
                    <span>
                      {getRequestStatusLabel(selectedService.setupRequest.status)}
                    </span>
                  </div>

                  <div>
                    <strong>Submitted</strong>
                    <span>
                      {selectedService.setupRequest.createdAtFormatted ||
                        "Recently"}
                    </span>
                  </div>
                </div>

                {selectedService.setupRequest.notes ? (
                  <div className="admin-modal-request-notes">
                    <strong>User Notes</strong>
                    <p>{selectedService.setupRequest.notes}</p>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="admin-modal-request-box">
                <span className="admin-hosting-eyebrow">
                  No User Request
                </span>
                <h3>No setup form submitted yet</h3>
                <p>
                  You can still update setup status manually, but user domain
                  details are not available yet.
                </p>
              </div>
            )}

            <form className="admin-setup-form" onSubmit={handleSaveSetup}>
              <div className="admin-form-grid">
                <label>
                  Setup Status
                  <select
                    name="setupStatus"
                    value={setupForm.setupStatus}
                    onChange={handleSetupFormChange}
                  >
                    {setupStatusOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  cPanel URL
                  <input
                    name="cpanelUrl"
                    value={setupForm.cpanelUrl}
                    onChange={handleSetupFormChange}
                    placeholder="https://server.example.com:2083"
                  />
                </label>

                <label>
                  cPanel Username
                  <input
                    name="cpanelUsername"
                    value={setupForm.cpanelUsername}
                    onChange={handleSetupFormChange}
                    placeholder="demo_user_7"
                  />
                </label>

                <label>
                  Server IP
                  <input
                    name="serverIp"
                    value={setupForm.serverIp}
                    onChange={handleSetupFormChange}
                    placeholder="123.123.123.123"
                  />
                </label>

                <label>
                  Nameserver 1
                  <input
                    name="nameserver1"
                    value={setupForm.nameserver1}
                    onChange={handleSetupFormChange}
                    placeholder="ns1.dohostingpay.com"
                  />
                </label>

                <label>
                  Nameserver 2
                  <input
                    name="nameserver2"
                    value={setupForm.nameserver2}
                    onChange={handleSetupFormChange}
                    placeholder="ns2.dohostingpay.com"
                  />
                </label>
              </div>

              <div className="admin-modal-warning">
                Save only real server/cPanel details for this exact user
                service. Requested domain:{" "}
                <strong>
                  {selectedService.setupRequest?.domainName || "Not submitted"}
                </strong>
                . SSL is required. Email setup is not mandatory.
              </div>

              <div className="admin-modal-actions">
                <button type="button" onClick={closeSetupModal}>
                  Cancel
                </button>

                <button type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Save Setup"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default AdminHostingSetup;